import { ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import type { ModuleField } from '@/types/domain';

/**
 * 表单里的标准选项库（变更类型、变更来源、发生阶段、问题分类、客户、部门…）。
 *
 * **抽出来是因为这套接线原先只写在模块中心**：同一个「变更类型」字段，
 * 在模块中心是从公司目录里选的下拉，在项目页却是个自由文本框——
 * 而人实际是在项目里填变更的（试用反馈 0819：「变更来源与类型无标准选择库，
 * 自主编辑叫法可能复杂多样，建议建立标准类型库选择」）。
 *
 * 库里本来就有：change-type 11 种、issue-stage 是 P01–P06。
 * 缺的从来不是数据，是第二处界面没接上。
 */

/** 字段 key → [后端选项集, 显示名, 说明] */
const OPTION_FIELDS: Readonly<Record<string, readonly [string, string, string]>> = {
  parentId: ['orgUnit', '所属部门', '从公司组织架构里选；留空表示暂不归属部门。'],
  linkedDfmeaId: ['dfmea', '关联 DFMEA', '选这条 PFMEA 对应的设计失效分析；没有就留空。'],
  changeType: ['changeType', '变更类型', '按公司变更类型目录选择，用于分类统计。'],
  changeSource: ['changeSource', '变更来源', '这次变更是谁提出来的。'],
  occurrenceStage: ['issueStage', '发生阶段', '问题/变更是在哪个 APQP 阶段暴露出来的。'],
  category: ['issueCategory', '问题分类', '按升级层级选择，决定问题往哪一级推。'],
  productCategory: ['issueProduct', '产品类别', '按工艺大类选择，用于按产线统计。'],
  customer: ['customers', '客户', '从客户清单选择（company.xlsx 的 64 家）；确实是新客户再手输。'],
};

/** 一次取回、进程内共享：两个视图来回切不用重复请求 */
const fieldOptions = ref<Record<string, Array<{ value: string; label: string }>>>({});

export function useFormOptions() {
  async function loadFormOptions(): Promise<void> {
    if (Object.keys(fieldOptions.value).length) return;
    try {
      const loaded = await eqsApi.formOptions();
      // 接口给了个 null / 数组 / 字符串也不能让它进来：
      // 直接赋值的话后面 Object.keys 会炸，整个抽屉白屏——
      // 而这只是「下拉取不到」，本该退回文本框继续录入
      fieldOptions.value = loaded && typeof loaded === 'object' && !Array.isArray(loaded)
        ? loaded
        : {};
    } catch {
      // 取不到就保持空——下面会退回成原来的输入框，不能因为下拉挂了就让人录不进去
      fieldOptions.value = {};
    }
  }

  /** 把字段定义里能换成下拉的换掉；换不了的原样返回 */
  function withStandardOptions(fields: readonly ModuleField[]): ModuleField[] {
    return fields.map((field) => {
      const mapped = OPTION_FIELDS[field.key];
      const list = mapped ? fieldOptions.value[mapped[0]] ?? [] : [];
      if (!mapped || !list.length) return field;
      return {
        ...field,
        label: mapped[1],
        type: 'select' as const,
        options: list.map((item) => item.value),
        optionLabels: Object.fromEntries(list.map((item) => [item.value, item.label])),
        help: field.help ?? mapped[2],
      };
    });
  }

  return { fieldOptions, loadFormOptions, withStandardOptions };
}
